/**
 * InboxEditorModal — create or edit a departmental unified inbox (Amendments 1/3).
 *
 * Stepped rather than one long form, because the steps have different audiences:
 * the distro and member mailboxes come from the customer's Microsoft tenant, the
 * access grants come from the CRM's own users, and the reply settings are a policy
 * decision. Mixing them produced a form where it was unclear which field affected
 * which system.
 *
 * The reply-identity rules are enforced here as well as on the server, and the
 * duplication is deliberate: the server returns 422
 * `reply_identity_distro_requires_distro` for an impossible combination, which is
 * correct but arrives after the admin has filled in five steps. The client makes
 * the impossible option unselectable instead.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../../../ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../../../ui/dialog';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { RadioGroup, RadioGroupItem } from '../../../ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Switch } from '../../../ui/switch';
import { MailboxEmailInput, StatusBadge } from '../MailboxEmailInput';
import FilterChipInput from '../FilterChipInput';
import { useAuth } from '../../../../context/AuthContext';

const STEPS = ['Name', 'Distro', 'Members', 'Access', 'Reply settings', 'Filters'];

/** Seed key for an inbox that has no server id yet. Never a real `inbox_id`. */
const NEW_INBOX_KEY = '__new__';

/**
 * The reply-identity rule, as a pure function of the draft being submitted.
 *
 * `reply_identity: 'distro'` without a distro is rejected by the server with 422
 * `reply_identity_distro_requires_distro`, on both create and update. An effect
 * below keeps the *live* draft legal as the distro comes and goes — but an effect
 * cannot keep a REQUEST legal, because adding or removing a mailbox auto-saves in
 * the same handler that changes the distro. The payload is built from the draft as
 * it is at that instant, the effect does not run until after the commit, and the
 * request has already left with the old identity.
 *
 * That is not hypothetical: removing a distro sent `distro_smtp: null` alongside
 * `reply_identity: 'distro'` and 422'd, which surfaced as a raw error toast on a
 * step the admin had not touched. So every payload is normalised through here
 * rather than trusting the draft to have caught up.
 *
 * Mirrors `_normalise_reply_settings` in `runtime/backend/routes/microsoft_inbox.py`
 * plus the client-only "new inbox that gains a distro defaults to distro identity"
 * rule. Keep the two in step.
 */
function legalReplySettings(next, editing) {
    const hasDistro = Boolean(next.distro_graph_user_id || next.distro_smtp);
    const settings = next.reply_settings || {};
    if (!hasDistro) {
        return { reply_identity: 'member', enforce_distro_reply: false };
    }
    if (settings.reply_identity === 'member' && !editing) {
        return { ...settings, reply_identity: 'distro' };
    }
    return settings;
}

/**
 * Normalise filter config for submission. Mirrors
 * `_normalise_filter_config` in `runtime/backend/routes/microsoft_inbox.py`.
 * Lowercases and dedupes here so the server receives clean data and the
 * UI shows consistent values.
 */
function legalFilterConfig(next) {
    const config = next.filter_config || {};
    return {
        mode: config.mode || 'off',
        email_addresses: [...new Set(
            (config.email_addresses || [])
                .map(e => e.trim().toLowerCase())
                .filter(Boolean)
        )].sort(),
        lexical_words: [...new Set(
            (config.lexical_words || [])
                .map(w => w.trim().toLowerCase())
                .filter(Boolean)
        )].sort(),
    };
}

/** A draft that is safe to send: whatever changed, plus a legal reply identity. */
function submittable(next, editing) {
    return {
        ...next,
        reply_settings: legalReplySettings(next, editing),
        filter_config: legalFilterConfig(next),
    };
}

const ROLES = [
    { value: 'viewer', label: 'Viewer — read only' },
    { value: 'responder', label: 'Responder — read and reply' },
    {
        value: 'manager',
        label: 'Manager — read, reply, remove mail from this view, and manage members and access',
    },
];

const ROLE_RANK = { viewer: 1, responder: 2, manager: 3 };

function emptyDraft() {
    return {
        name: '',
        distro_graph_user_id: null,
        distro_smtp: null,
        member_user_ids: [],
        member_graph_ids: [],
        member_smtps: [],
        access_grants: [],
        reply_settings: { reply_identity: 'distro', enforce_distro_reply: true },
        filter_config: { mode: 'off', email_addresses: [], lexical_words: [] },
    };
}

export default function InboxEditorModal({
    open,
    onOpenChange,
    inbox,
    employees = [],
    onSave,
    isSaving,
    mailboxTokens = [],
    accessRequests = [],
    // Connections already attached to this inbox. Carries the Google delegated
    // grants that `mailboxTokens` structurally cannot — see `MailboxEmailInput`.
    mailboxConnections = [],
    onRequestAccess,
    onResend,
    onRevoke,
    onCancel,
    isRequesting = false,
    isCancelling = false,
    onAutoSave,
    // Issue #662: subset of STEPS to render, in STEPS order (e.g.
    // ['Members', 'Access'] for the manager-reachable per-inbox settings
    // surface). Null/absent renders all six steps — the admin wizard — so
    // every existing consumer and test keeps its exact behavior.
    visibleSteps = null,
}) {
    const editing = Boolean(inbox);
    const { user } = useAuth();
    const [step, setStep] = useState(0);
    const [draft, setDraft] = useState(emptyDraft);

    // The visible step list (Issue #662 step-hiding). Local indices index into
    // THIS array; content blocks below key off the label, not a hard-coded
    // index, so a hidden step never shifts another step's content and the
    // wizard never lands on a blank page.
    const steps =
        Array.isArray(visibleSteps) && visibleSteps.length
            ? STEPS.filter(label => visibleSteps.includes(label))
            : STEPS;

    // Which inbox the draft below was seeded from. Re-seeding is a destructive act
    // — it resets the wizard to step 1 and discards whatever the admin has typed —
    // so it must happen exactly once per editing session, not on every new object
    // identity of `inbox`.
    //
    // The distinction matters because adding a distro or member mailbox AUTO-SAVES
    // (see `onAutoSave` in InboxSettingsTab), and on a new inbox that first add is a
    // CREATE: the parent then calls `setEditing(newInbox)`, so `inbox` goes from
    // null to an object mid-session. Keyed on the object, this effect read that as
    // "a different inbox arrived" and threw the admin back to step 1 from step 2 or
    // 3, wiping the step they were on the moment they added their first mailbox.
    // Adopting the id of the inbox we just created is a CONTINUATION of the same
    // session, so it is explicitly excluded here.
    const seededFor = useRef(null);

    useEffect(() => {
        if (!open) {
            // Cleared on close so reopening always re-seeds, including for the
            // same inbox.
            seededFor.current = null;
            return;
        }

        const key = inbox?.inbox_id ?? NEW_INBOX_KEY;

        // The auto-create hand-off: same session, now with a server id.
        if (seededFor.current === NEW_INBOX_KEY && inbox?.inbox_id) {
            seededFor.current = key;
            return;
        }
        // Already seeded from this inbox — a refetch or an auto-save round-trip,
        // not a switch to a different one.
        if (seededFor.current === key) return;

        seededFor.current = key;
        setStep(0);
        setDraft(
            inbox
                ? {
                      name: inbox.name || '',
                      distro_graph_user_id: inbox.distro_graph_user_id || null,
                      distro_smtp: inbox.distro_smtp || null,
                      member_user_ids: inbox.member_user_ids || [],
                      member_graph_ids: inbox.member_graph_ids || [],
                      member_smtps: inbox.member_smtps || [],
                      access_grants: inbox.access_grants || [],
                      reply_settings: {
                          reply_identity: inbox.reply_settings?.reply_identity || 'distro',
                          enforce_distro_reply:
                              inbox.reply_settings?.enforce_distro_reply !== false,
                      },
                      filter_config: {
                          mode: inbox.filter_config?.mode || 'off',
                          email_addresses: inbox.filter_config?.email_addresses || [],
                          lexical_words: inbox.filter_config?.lexical_words || [],
                      },
                  }
                : emptyDraft()
        );
    }, [inbox, open]);

    const hasDistro = Boolean(draft.distro_graph_user_id || draft.distro_smtp);

    // Keep the LIVE draft's identity legal as the distro comes and goes, so the
    // radio on step 5 shows the truth. This does NOT protect outgoing requests —
    // it runs after the commit, and an auto-save fires inside the same handler
    // that changes the distro. `submittable()` is what keeps payloads legal; this
    // keeps the UI honest. Both apply the same rule, deliberately.
    useEffect(() => {
        setDraft(prev => {
            const settings = prev.reply_settings || {};
            if (!hasDistro && settings.reply_identity !== 'member') {
                return {
                    ...prev,
                    reply_settings: { reply_identity: 'member', enforce_distro_reply: false },
                };
            }
            if (hasDistro && settings.reply_identity === 'member' && !editing) {
                return { ...prev, reply_settings: { ...settings, reply_identity: 'distro' } };
            }
            return prev;
        });
    }, [editing, hasDistro]);

    const currentUserId = user?.user_id;
    const currentUserExistingRole = inbox?.access_grants?.find(
        grant => grant.user_id === currentUserId
    )?.role;
    const currentUserDraftRole = draft.access_grants.find(
        grant => grant.user_id === currentUserId
    )?.role;
    const selfDemoting = Boolean(
        editing &&
            currentUserExistingRole &&
            (ROLE_RANK[currentUserDraftRole] || 0) < ROLE_RANK[currentUserExistingRole]
    );

    const setGrant = (userId, role) => {
        setDraft(prev => {
            const grants = prev.access_grants.filter(g => g.user_id !== userId);
            if (role) grants.push({ user_id: userId, role });
            return { ...prev, access_grants: grants };
        });
    };

    /**
     * Everyone the Access step must show: the people picker, PLUS anyone who
     * already holds a grant but is missing from it.
     *
     * The step pairs each row in `employees` with its grant, so a grant held
     * by somebody absent from that list rendered NOWHERE — the modal
     * under-reported who could open the inbox, and the grant could not be
     * revoked from the surface that displayed it. That happened whenever the
     * two configuration modals were fed different lists, which is exactly
     * what the per-inbox settings page did before it gained a manager-legal
     * picker: it derived `employees` from the inbox's MEMBERS, so a
     * non-member's grant was invisible.
     *
     * Keeping this union means the step is correct even if a caller passes a
     * partial list — the displayed set can never be smaller than the granted
     * set, whatever the picker returns.
     */
    const grantablePeople = useMemo(() => {
        const known = new Set(employees.map(e => e.user_id));
        const orphanGrants = (draft.access_grants || [])
            .filter(g => g.user_id && !known.has(g.user_id))
            .map(g => ({
                user_id: g.user_id,
                // The picker is where names come from; a grant carries none.
                // Saying so beats printing a bare uuid with no explanation.
                name: g.name || g.email || 'User outside this list',
                email: g.email || '',
            }));
        return [...employees, ...orphanGrants];
    }, [employees, draft.access_grants]);

    const canAdvance = steps[0] !== 'Name' || step !== 0 || draft.name.trim().length > 0;
    const last = step === steps.length - 1;

    const save = async () => {
        if (!draft.name.trim()) {
            toast.error('Give the inbox a name');
            return;
        }
        try {
            await onSave?.(
                submittable(
                    {
                        ...draft,
                        name: draft.name.trim(),
                        distro_smtp: draft.distro_smtp || null,
                        distro_graph_user_id: draft.distro_graph_user_id || null,
                    },
                    editing
                )
            );
            toast.success(editing ? 'Inbox updated' : 'Inbox created');
            onOpenChange(false);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const messages = {
                inbox_name_taken: 'Another inbox already uses that name',
                reply_identity_distro_requires_distro:
                    'Choose a distro mailbox, or set members to reply as themselves',
            };
            toast.error(messages[detail] || detail || 'Could not save the inbox');
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
                data-testid="inbox-editor-modal"
            >
                <DialogHeader>
                    <DialogTitle>{editing ? `Edit ${inbox.name}` : 'New unified inbox'}</DialogTitle>
                    <DialogDescription className="sr-only">
                        Configure the shared address, pooled mailboxes, access grants, and reply settings for this inbox.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex items-center gap-1 py-2">
                    {steps.map((label, index) => (
                        <button
                            key={label}
                            type="button"
                            onClick={() => setStep(index)}
                            data-testid={`editor-step-${index}`}
                            className={`text-xs px-2 py-1 rounded-full transition-colors ${
                                index === step
                                    ? 'bg-stone-900 text-white'
                                    : 'text-stone-500 hover:bg-stone-100'
                            }`}
                        >
                            {index + 1}. {label}
                        </button>
                    ))}
                </div>

                <div className="py-2 min-h-[220px]">
                    {steps[step] === 'Name' && (
                        <div>
                            <Label className="text-xs">Inbox name</Label>
                            <Input
                                value={draft.name}
                                onChange={e => setDraft(p => ({ ...p, name: e.target.value }))}
                                placeholder="HR, Sales, Support…"
                                className="mt-1"
                                data-testid="inbox-name-input"
                            />
                            <p className="text-xs text-stone-400 mt-2">
                                Shown in the mailbox switcher. Must be unique for your
                                organisation.
                            </p>
                        </div>
                    )}

                    {steps[step] === 'Distro' && (
                        <div>
                            <p className="text-xs text-stone-400 mt-0.5 mb-2">
                                Type the email address of the shared mailbox. The mailbox
                                owner will receive an email asking them to grant access.
                            </p>
                            <MailboxEmailInput
                                label="Distro / shared mailbox (optional)"
                                selected={draft.distro_smtp ? [draft.distro_smtp] : []}
                                onChange={emails => {
                                    // Normalised at build time, not left to the effect:
                                    // removing the distro here would otherwise send
                                    // `distro_smtp: null` alongside the old
                                    // `reply_identity: 'distro'` and 422.
                                    const updated = submittable(
                                        {
                                            ...draft,
                                            distro_smtp: emails[0] || null,
                                            distro_graph_user_id: null,
                                        },
                                        editing
                                    );
                                    setDraft(prev => ({
                                        ...prev,
                                        distro_smtp: emails[0] || null,
                                        distro_graph_user_id: null,
                                    }));
                                    if (onAutoSave) onAutoSave(updated);
                                }}
                                tokens={mailboxTokens}
                                accessRequests={accessRequests}
                                connections={mailboxConnections}
                                onRequestAccess={onRequestAccess}
                                onResend={onResend}
                                onRevoke={onRevoke}
                                onCancel={onCancel}
                                isCancelling={isCancelling}
                                isRequesting={isRequesting}
                            />
                        </div>
                    )}

                    {steps[step] === 'Members' && (
                        <div>
                            <p className="text-xs text-stone-400 mt-0.5 mb-2">
                                Individual mailboxes pooled into this inbox. Both their Inbox
                                and Sent Items are monitored — Sent Items is how a reply is
                                detected at all, including one sent from Outlook.
                            </p>
                            <MailboxEmailInput
                                label="Member mailboxes"
                                selected={draft.member_smtps}
                                onChange={emails => {
                                    // Same normalisation as the distro step. A member
                                    // add on an inbox with no distro must not carry the
                                    // seed default `reply_identity: 'distro'`.
                                    const updated = submittable(
                                        {
                                            ...draft,
                                            member_smtps: emails,
                                            member_graph_ids: [],
                                        },
                                        editing
                                    );
                                    setDraft(prev => ({
                                        ...prev,
                                        member_smtps: emails,
                                        member_graph_ids: [],
                                    }));
                                    if (onAutoSave) onAutoSave(updated);
                                }}
                                tokens={mailboxTokens}
                                accessRequests={accessRequests}
                                connections={mailboxConnections}
                                onRequestAccess={onRequestAccess}
                                onResend={onResend}
                                onRevoke={onRevoke}
                                onCancel={onCancel}
                                isCancelling={isCancelling}
                                isRequesting={isRequesting}
                            />
                        </div>
                    )}

                    {steps[step] === 'Access' && (
                        <div>
                            <Label className="text-xs">Access grants</Label>
                            <p className="text-xs text-stone-400 mt-0.5 mb-2">
                                Every user, including organisation admins, needs a grant to open this
                                inbox. Revoking a grant takes effect on that person&apos;s next request.
                            </p>
                            {selfDemoting && (
                                <p
                                    className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-2"
                                    data-testid="self-demotion-warning"
                                >
                                    If no active manager remains on this inbox, an org admin can
                                    recover it via the admin console.
                                </p>
                            )}
                            <div className="max-h-56 overflow-y-auto border border-stone-200 rounded-lg divide-y divide-stone-100">
                                {grantablePeople.map(employee => {
                                    const grant = draft.access_grants.find(
                                        g => g.user_id === employee.user_id
                                    );
                                    return (
                                        <div
                                            key={employee.user_id}
                                            className="flex items-center gap-2 px-3 py-2"
                                            data-testid={`grant-row-${employee.user_id}`}
                                        >
                                            <span className="flex-1 min-w-0">
                                                <span className="block text-sm text-stone-800 truncate">
                                                    {employee.name || employee.email}
                                                </span>
                                                <span className="block text-xs text-stone-400 truncate">
                                                    {employee.email}
                                                    {employee.is_admin ? ' · admin' : ''}
                                                </span>
                                            </span>
                                            <Select
                                                value={grant?.role || 'none'}
                                                onValueChange={value =>
                                                    setGrant(
                                                        employee.user_id,
                                                        value === 'none' ? null : value
                                                    )
                                                }
                                            >
                                                <SelectTrigger className="w-44 h-8 text-xs">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="none">No access</SelectItem>
                                                    {ROLES.map(role => (
                                                        <SelectItem
                                                            key={role.value}
                                                            value={role.value}
                                                        >
                                                            {role.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    );
                                })}
                                {employees.length === 0 && (
                                    <p className="px-3 py-3 text-xs text-stone-400">
                                        No other users in this organisation yet.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {steps[step] === 'Reply settings' && (
                        <div className="space-y-4">
                            <div>
                                <Label className="text-xs">Reply identity</Label>
                                <RadioGroup
                                    value={draft.reply_settings.reply_identity}
                                    onValueChange={value =>
                                        setDraft(p => ({
                                            ...p,
                                            reply_settings: {
                                                ...p.reply_settings,
                                                reply_identity: value,
                                            },
                                        }))
                                    }
                                    className="mt-2 space-y-2"
                                >
                                    {/* Hidden, not merely disabled, when there is no distro:
                                        an option that can never be chosen is noise, and the
                                        server rejects it with a 422 anyway. */}
                                    {hasDistro && (
                                        <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                                            <RadioGroupItem
                                                value="distro"
                                                data-testid="reply-identity-distro"
                                            />
                                            Reply as {draft.distro_smtp || 'the distro address'}
                                        </label>
                                    )}
                                    <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                                        <RadioGroupItem
                                            value="member"
                                            data-testid="reply-identity-member"
                                        />
                                        Members reply as themselves
                                    </label>
                                </RadioGroup>
                                {!hasDistro && (
                                    <p
                                        className="text-xs text-stone-400 mt-1"
                                        data-testid="reply-identity-forced"
                                    >
                                        This inbox has no distro address, so members always reply
                                        as themselves.
                                    </p>
                                )}
                            </div>

                            {hasDistro && (
                                <div className="flex items-start gap-3">
                                    <Switch
                                        checked={draft.reply_settings.enforce_distro_reply}
                                        onCheckedChange={value =>
                                            setDraft(p => ({
                                                ...p,
                                                reply_settings: {
                                                    ...p.reply_settings,
                                                    enforce_distro_reply: Boolean(value),
                                                },
                                            }))
                                        }
                                        data-testid="enforce-distro-reply"
                                    />
                                    <div>
                                        <p className="text-sm text-stone-800">
                                            Only allow replies from the distro address
                                        </p>
                                        <p className="text-xs text-stone-400 mt-0.5">
                                            Personal-address replies sent from Outlook are still
                                            detected and flagged — this governs the composer in
                                            NurtureCRM, not detection.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {steps[step] === 'Filters' && (
                        <div className="space-y-4">
                            <div>
                                <Label className="text-xs">Filter mode</Label>
                                <p className="text-xs text-stone-400 mt-0.5 mb-2">
                                    Choose how inbound emails are filtered for this inbox.
                                    Filtered emails are dropped entirely and never stored.
                                </p>
                                <RadioGroup
                                    value={draft.filter_config.mode}
                                    onValueChange={value =>
                                        setDraft(p => ({
                                            ...p,
                                            filter_config: {
                                                ...p.filter_config,
                                                mode: value,
                                            },
                                        }))
                                    }
                                    className="mt-2 space-y-2"
                                >
                                    <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                                        <RadioGroupItem value="off" data-testid="filter-mode-off" />
                                        Off — store all inbound emails
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                                        <RadioGroupItem value="blacklist" data-testid="filter-mode-blacklist" />
                                        Blacklist — drop emails matching any rule below
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                                        <RadioGroupItem value="whitelist" data-testid="filter-mode-whitelist" />
                                        Whitelist — keep only emails matching any rule below
                                    </label>
                                </RadioGroup>
                            </div>

                            {draft.filter_config.mode !== 'off' && (
                                <>
                                    <div>
                                        <Label className="text-xs">Email addresses</Label>
                                        <p className="text-xs text-stone-400 mt-0.5 mb-2">
                                            {draft.filter_config.mode === 'blacklist'
                                                ? 'Drop emails from these senders. Use @domain.com to match an entire domain.'
                                                : 'Keep emails from these senders only. Use @domain.com to match an entire domain.'}
                                        </p>
                                        <FilterChipInput
                                            items={draft.filter_config.email_addresses}
                                            onAdd={item => setDraft(p => ({
                                                ...p,
                                                filter_config: {
                                                    ...p.filter_config,
                                                    email_addresses: [...p.filter_config.email_addresses, item],
                                                },
                                            }))}
                                            onRemove={idx => setDraft(p => ({
                                                ...p,
                                                filter_config: {
                                                    ...p.filter_config,
                                                    email_addresses: p.filter_config.email_addresses.filter((_, i) => i !== idx),
                                                },
                                            }))}
                                            onBulkAdd={words => setDraft(p => ({
                                                ...p,
                                                filter_config: {
                                                    ...p.filter_config,
                                                    email_addresses: [...p.filter_config.email_addresses, ...words],
                                                },
                                            }))}
                                            placeholder="e.g. spammer@example.com or @marketing.com"
                                            testIdPrefix="filter-email"
                                        />
                                    </div>

                                    <div>
                                        <Label className="text-xs">Lexical words</Label>
                                        <p className="text-xs text-stone-400 mt-0.5 mb-2">
                                            {draft.filter_config.mode === 'blacklist'
                                                ? 'Drop emails whose subject or body contains any of these words (case-insensitive).'
                                                : 'Keep emails whose subject or body contains any of these words (case-insensitive).'}
                                        </p>
                                        <FilterChipInput
                                            items={draft.filter_config.lexical_words}
                                            onAdd={item => setDraft(p => ({
                                                ...p,
                                                filter_config: {
                                                    ...p.filter_config,
                                                    lexical_words: [...p.filter_config.lexical_words, item],
                                                },
                                            }))}
                                            onRemove={idx => setDraft(p => ({
                                                ...p,
                                                filter_config: {
                                                    ...p.filter_config,
                                                    lexical_words: p.filter_config.lexical_words.filter((_, i) => i !== idx),
                                                },
                                            }))}
                                            onBulkAdd={words => setDraft(p => ({
                                                ...p,
                                                filter_config: {
                                                    ...p.filter_config,
                                                    lexical_words: [...p.filter_config.lexical_words, ...words],
                                                },
                                            }))}
                                            placeholder="e.g. unsubscribe, lottery, invoice"
                                            testIdPrefix="filter-word"
                                        />
                                    </div>

                                    <div className="rounded-lg border border-stone-200 bg-stone-50 p-3">
                                        <p className="text-xs text-stone-500">
                                            {draft.filter_config.mode === 'blacklist'
                                                ? 'An inbound email is dropped if its sender matches any listed email address/domain OR its subject/body contains any listed word. Emails that don\'t match any rule are stored normally.'
                                                : 'An inbound email is kept only if its sender matches any listed email address/domain OR its subject/body contains any listed word. Emails that match no rule are dropped entirely.'}
                                        </p>
                                    </div>
                                </>
                            )}

                            {draft.filter_config.mode === 'off' && (
                                <p className="text-xs text-stone-400" data-testid="filter-mode-off-hint">
                                    No filtering is active. All inbound emails for this inbox are stored.
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-stone-200">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setStep(s => Math.max(0, s - 1))}
                        disabled={step === 0}
                    >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Back
                    </Button>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                            <X className="w-4 h-4 mr-1" />
                            Cancel
                        </Button>
                        {last ? (
                            <Button
                                size="sm"
                                className="bg-stone-900 hover:bg-stone-800"
                                onClick={save}
                                disabled={isSaving}
                                data-testid="inbox-editor-save"
                            >
                                {isSaving ? (
                                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                ) : (
                                    <Check className="w-4 h-4 mr-1" />
                                )}
                                {editing ? 'Save changes' : 'Create inbox'}
                            </Button>
                        ) : (
                            <Button
                                size="sm"
                                onClick={() => setStep(s => s + 1)}
                                disabled={!canAdvance}
                                data-testid="inbox-editor-next"
                            >
                                Next
                                <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
